import type { LayerCatalogItem } from '../api'
import { NO_TOOLS, SERIES_DEPTH, type SeriesFrame } from './seriesCatalog'
import { SERIES_ISOTHERM } from './seriesCatalog'

/**
 * Состав серии — производная КАТАЛОГА СЛОЁВ, а не список руками.
 *
 * Решение владельца 07.09.2026: «абсолютно все картинки должны попадать в
 * серию; под всеми имеются в виду картинки со всеми оверлеями по отдельности».
 * До этого состав был расписан на два вида (сельдь и сайра) по пять кадров.
 *
 * Список руками протух бы на первом же новом слое, и протух бы МОЛЧА: серия
 * продолжала бы собираться, просто без него, а узнали бы мы об этом от
 * пользователя через месяцы. То же рассуждение, что у `model_inputs.py`:
 * незнакомый слой обязан ошибаться в сторону лишнего кадра, а не пропущенного.
 */

/**
 * Подложка кадров оверлея.
 *
 * Оверлей поверх ПУСТОЙ карты читается плохо: лёд, рубеж и промрайон надо на
 * что-то положить, иначе кадр «Границы ИЭЗ» это линии в белизне. Батиметрия
 * выбрана потому, что она статична (поле есть всегда, ждать нечего), покрывает
 * весь бассейн и не спорит цветом с тем, что на неё кладут.
 */
export const OVERLAY_BASE_LAYER = 'bathymetry'

/** Кадр, с которого серия начинается: течения под инструментами. */
const LEAD_LAYER = 'currents'

export interface ComposeOptions {
  /** Каталог слоёв ЭТОГО кабинета: он же единственный источник прав. */
  layers: LayerCatalogItem[]
  speciesId: number | null
  /** Рабочая глубина кадров, знающих глубину. */
  depth?: number
}

/** Кадр без единого инструмента: чистое поле своего слоя. */
function plain(
  id: string,
  kind: SeriesFrame['kind'],
  layer: string,
  labelKey: string,
  depth: number | null,
  overlays: string[] = [],
): SeriesFrame {
  return { id, kind, layer, overlays, labelKey, depth, ...NO_TOOLS }
}

export function composeSeries({ layers, speciesId, depth = SERIES_DEPTH }: ComposeOptions): SeriesFrame[] {
  // Пустой каталог — «ещё не знаем», а не «ничего нельзя»: выдумывать состав
  // здесь значило бы обещать кадры слоёв, которых кабинету могли не выдать.
  if (!layers.length) return []

  const paintable = layers.filter(l => l.render === 'tiles' && !l.overlay)
  const overlays = layers.filter(l => l.overlay)

  const frames: SeriesFrame[] = []

  // ── Кадр слоя. Первым идут течения под инструментами: это и была серия до
  // 07.09.2026, по этому кадру читают карту сноса, и прежние архивы обязаны
  // остаться сравнимыми с новыми.
  const lead = paintable.find(l => l.id === LEAD_LAYER)
  if (lead) {
    const iso = speciesId != null ? SERIES_ISOTHERM[speciesId] : undefined
    frames.push(
      iso != null
        ? {
            id: lead.id, kind: 'layer', layer: lead.id, overlays: [],
            labelKey: `layers.${lead.id}`,
            depth: lead.has_depth ? depth : null,
            isotherm: iso,
            // Поверхность и смежные на кадрах серии выключены (владелец
            // 2026-08-19): состав архива не меняется от появления новых
            // тумблеров, иначе прежние отчёты стало бы не с чем сравнивать.
            isothermSurface: null,
            isothermSpread: null,
            fronts: 6,
            frontsUnit: '100km',
            isobathStep: 10,
            waveStep: 0.5,
            hsiDrift: true,
          }
        // Порог изотермы называет владелец ПО ВИДУ. Выдумать его нельзя, а
        // поставить чужой — значит подписать кадр числом, которого никто не
        // выбирал; вид без порога получает чистое поле течений.
        : plain(lead.id, 'layer', lead.id, `layers.${lead.id}`, lead.has_depth ? depth : null),
    )
  }

  for (const l of paintable) {
    if (l.id === LEAD_LAYER) continue
    // Оценка считается ПО ВИДУ: без вида слоя не существует вовсе. Гасить
    // из-за этого всю серию незачем — прочие кадры видонезависимы.
    if (l.kind === 'model' && speciesId == null) continue
    frames.push(plain(l.id, 'layer', l.id, `layers.${l.id}`, l.has_depth ? depth : null))
  }

  // ── Кадр оверлея: ровно ОДИН оверлей поверх подложки. По одному, а не все
  // разом, — это и есть просьба владельца: разобрать, что даёт каждый.
  for (const o of overlays) {
    frames.push(plain(o.id, 'overlay', OVERLAY_BASE_LAYER, `layers.${o.id}`, null, [o.id]))
  }

  return frames
}
